import {
  deckStats,
  foilType,
  formatUsd,
  groupDeck,
  matchesFilter,
  priceFor,
  type DeckGrouping,
  type DeckSort,
  type DeckViewStyle,
} from '@mtg/shared';
import { useState, type PointerEvent, type ReactNode } from 'react';
import { Link } from 'wouter';
import { useDeckPrefs } from '../deckPrefs.ts';
import { plural } from '../format.ts';
import type { Item } from './CardGroups.tsx';
import { CardImage } from './CardImage.tsx';
import { FinishBadge } from './FinishBadge.tsx';
import { ManaCost, Pip } from './ManaCost.tsx';

const VIEW_LABEL: Record<DeckViewStyle, string> = { list: 'List', stacks: 'Stacks', grid: 'Grid' };
const GROUP_LABEL: Record<DeckGrouping, string> = { type: 'Type', manaValue: 'Mana value', color: 'Color', none: 'Nothing' };
const SORT_LABEL: Record<DeckSort, string> = { name: 'Name', manaValue: 'Mana value', price: 'Price' };

const COLOR_NAME: Record<string, string> = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' };

const count = (items: Item[]) => items.reduce((n, i) => n + i.quantity, 0);

/**
 * A deck as a list, stacks of cards, or a grid, grouped and sorted as chosen
 * (remembered per device), with a filter box, deck stats, and a large
 * preview of the card under the pointer or the one picked. Owner actions
 * for the picked card go in the preview.
 */
export function DeckView({
  items,
  commanders = [],
  linkCards = true,
  showPrices = true,
  actions,
}: {
  items: Item[];
  commanders?: string[];
  /** Link to our card page (owner), or to Scryfall (shared page). */
  linkCards?: boolean;
  /** Prices and sorting by price; off on the pages friends see. */
  showPrices?: boolean;
  actions?: (item: Item) => ReactNode;
}) {
  const [savedPrefs, setPrefs] = useDeckPrefs();
  const prefs = !showPrices && savedPrefs.sort === 'price' ? { ...savedPrefs, sort: 'name' as const } : savedPrefs;
  const sorts = (Object.keys(SORT_LABEL) as DeckSort[]).filter((s) => showPrices || s !== 'price');
  const [filter, setFilter] = useState('');
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const [hovered, setHovered] = useState<Item | null>(null);

  const shown = items.filter((i) => matchesFilter(i.card, filter, foilType(i.card, i.finish)?.label));
  const sections = groupDeck(shown, { groupBy: prefs.groupBy, sort: prefs.sort, commanders });
  const picked = items.find((i) => i.key === pickedKey) ?? null;
  const preview = hovered ?? picked ?? sections[0]?.items[0] ?? null;

  // Hover previews with a mouse only; a tap picks the card and opens the preview.
  const cardProps = (item: Item) => ({
    type: 'button' as const,
    className: `deck-card${item.key === pickedKey ? ' picked' : ''}`,
    onClick: () => setPickedKey(item.key),
    onPointerEnter: (e: PointerEvent) => e.pointerType === 'mouse' && setHovered(item),
    onPointerLeave: () => setHovered(null),
    'aria-label': `${item.quantity > 1 ? `${item.quantity} ` : ''}${item.card.name}`,
  });

  return (
    <div className="deck-view">
      <details className="deck-stats panel" open={prefs.stats} onToggle={(e) => setPrefs({ stats: e.currentTarget.open })}>
        <summary>Deck stats</summary>
        <DeckStatsStrip items={items} />
      </details>

      <div className="deck-toolbar">
        <div className="segmented" role="group" aria-label="View">
          {(Object.keys(VIEW_LABEL) as DeckViewStyle[]).map((v) => (
            <button key={v} type="button" aria-pressed={prefs.view === v} onClick={() => setPrefs({ view: v })}>
              {VIEW_LABEL[v]}
            </button>
          ))}
        </div>
        <label className="field">
          <span>Group by</span>
          <select value={prefs.groupBy} onChange={(e) => setPrefs({ groupBy: e.target.value as DeckGrouping })}>
            {(Object.keys(GROUP_LABEL) as DeckGrouping[]).map((g) => (
              <option key={g} value={g}>
                {GROUP_LABEL[g]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Sort by</span>
          <select value={prefs.sort} onChange={(e) => setPrefs({ sort: e.target.value as DeckSort })}>
            {sorts.map((s) => (
              <option key={s} value={s}>
                {SORT_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="field grow">
          <span>Filter</span>
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Name, type, rules text or foil"
            autoComplete="off"
            autoCapitalize="off"
            enterKeyHint="search"
          />
        </label>
      </div>
      {filter.trim() && (
        <p className="muted small" role="status">
          {count(shown)} of {plural(count(items), 'card')} match
        </p>
      )}

      <div className="deck-body">
        {preview && (
          <aside className={`deck-preview${picked ? ' open' : ''}`} aria-label="Card preview">
            <Preview
              key={preview.key}
              item={preview}
              linkCards={linkCards}
              showPrices={showPrices}
              actions={preview.key === pickedKey ? actions : undefined}
              onClose={() => setPickedKey(null)}
            />
          </aside>
        )}
        <div className={`deck-sections view-${prefs.view}`}>
          {sections.length === 0 && <p className="muted">No cards match.</p>}
          {sections.map((s) => (
            <section key={s.title} className="deck-section">
              <h3>
                {s.title} <span className="muted">({count(s.items)})</span>
              </h3>
              {prefs.view === 'list' ? (
                <ul className="deck-list">
                  {s.items.map((item) => (
                    <li key={item.key}>
                      <button {...cardProps(item)}>
                        <span className="deck-qty">{item.quantity}</span>
                        <span className="deck-name">
                          <span className="deck-name-text">{item.card.name}</span>
                          <FinishBadge card={item.card} finish={item.finish} />
                        </span>
                        {prefs.sort === 'price' ? <span className="muted small">{priceText(item)}</span> : <ManaCost cost={item.card.manaCost} />}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : prefs.view === 'stacks' ? (
                <ul className="deck-pile">
                  {s.items.map((item) => (
                    <li key={item.key}>
                      <button {...cardProps(item)}>
                        <CardImage card={item.card} />
                        {item.quantity > 1 && <span className="qty">×{item.quantity}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="card-grid">
                  {s.items.map((item) => (
                    <li key={item.key} className="tile">
                      <button {...cardProps(item)}>
                        <CardImage card={item.card} />
                      </button>
                      {item.quantity > 1 && <span className="qty">×{item.quantity}</span>}
                      <div className="tile-meta">
                        <strong>{item.card.name}</strong>
                        <span className="muted small">
                          {item.card.setCode.toUpperCase()} {item.card.collectorNumber}
                          <FinishBadge card={item.card} finish={item.finish} />
                        </span>
                        {showPrices && <span className="muted small">{priceText(item)}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function Preview({
  item,
  linkCards,
  showPrices,
  actions,
  onClose,
}: {
  item: Item;
  linkCards: boolean;
  showPrices: boolean;
  actions?: (item: Item) => ReactNode;
  onClose: () => void;
}) {
  const [back, setBack] = useState(false);
  const { card } = item;
  const showBack = back && !!card.imageBack;
  return (
    <div className="stack">
      <button type="button" className="btn small preview-close" onClick={onClose}>
        Close
      </button>
      <CardImage card={card} back={showBack} />
      {card.imageBack && (
        <button type="button" className="btn small" onClick={() => setBack(!back)}>
          {showBack ? 'Show front' : 'Show back'}
        </button>
      )}
      <div className="preview-text">
        <p className="preview-head">
          <strong>
            {item.quantity > 1 && <span className="muted">{item.quantity}× </span>}
            {card.name}
          </strong>
          <ManaCost cost={card.manaCost} />
        </p>
        <p className="muted small">{card.typeLine}</p>
        <p className="small">
          {card.setCode.toUpperCase()} {card.collectorNumber}
          <FinishBadge card={item.card} finish={item.finish} />
          {item.lang && item.lang !== 'en' && <span className="badge">{item.lang.toUpperCase()}</span>}
          {item.condition && item.condition !== 'NM' && <span className="badge">{item.condition}</span>}
          {showPrices && <> · {priceText(item)}</>}
        </p>
        <p className="small">
          {linkCards ? (
            <Link href={`/cards/${card.scryfallId}`}>Card page</Link>
          ) : (
            <a href={card.scryfallUri} target="_blank" rel="noreferrer">
              On Scryfall
            </a>
          )}
        </p>
      </div>
      {actions?.(item)}
    </div>
  );
}

function DeckStatsStrip({ items }: { items: Item[] }) {
  const stats = deckStats(items);
  const tallest = Math.max(1, ...stats.curve.map((c) => c.count));
  const totalPips = stats.colors.reduce((n, c) => n + c.pips, 0);
  return (
    <div className="deck-stats-body">
      <div>
        <h4>Types</h4>
        <ul className="stat-list inline">
          {stats.types.map((t) => (
            <li key={t.group}>
              {t.group} <strong>{t.count}</strong>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4>
          Mana curve <span className="muted small">· average {stats.averageManaValue.toFixed(2)}</span>
        </h4>
        <ol className="curve" aria-label="Non-land cards by mana value">
          {stats.curve.map((c) => (
            <li key={c.manaValue} aria-label={`Mana value ${c.manaValue}: ${c.count}`}>
              <span className="curve-count">{c.count || ''}</span>
              <span className="curve-track">
                <span className="curve-bar" style={{ height: `${(c.count / tallest) * 100}%` }} />
              </span>
              <span className="curve-label">{c.manaValue}</span>
            </li>
          ))}
        </ol>
      </div>
      {stats.colors.length > 0 && (
        <div>
          <h4>Colors</h4>
          <ul className="stat-list">
            {stats.colors.map((c) => (
              <li key={c.color}>
                <Pip symbol={c.color} /> {COLOR_NAME[c.color]}: {totalPips ? Math.round((c.pips / totalPips) * 100) : 0}% of symbols ·{' '}
                {plural(c.sources, 'land')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function priceText(item: Item) {
  const each = priceFor(item.card, item.finish);
  if (each === null) return 'No price';
  return item.quantity > 1 ? `${formatUsd(each)} each` : formatUsd(each);
}
